import { formatPlanTokens, zonedDateKey, type CapacityLiveDto, type FairUseRowDto } from '@dbrain/shared';
import { Alert, Badge, Button, Card, Dialog, Icons, Progress, ScrollArea, Select, Skeleton, TextInput } from '@dbrain/ui';
import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import { useId, useState, type ReactNode } from 'react';
import { useTranslation } from 'react-i18next';
import { api } from '../../api/client.js';
import { useCompanyZone } from '../../api/hooks.js';
import { ErrorAlert } from '../../components/ErrorAlert.js';
import { useTimes } from '../../components/status.js';
import { formatPercent, humanTime } from '@dbrain/app-core/format';
import { canMailFairUse, dailyShare, mailState, queueMix, tokenShares, waitSeconds } from './capacity-live.js';

/**
 * Platform › Kapasite where the edition has plans (Phase 16a, ADR-0023, 06 §28.5, D-271…D-273; B2cPlatformKapasite):
 * the live tiles (the shared model server, the personal requests against their share, the queue, today's tokens by
 * plan), the share and the queue's order, the e-mail sending's last result (D-272), and fair use: who has been near
 * their daily limit for days, with a mail the operator reads before sending. Nothing here acts by itself.
 */
const LIVE_KEY = ['platform', 'capacity', 'live'] as const;
const FAIR_USE_KEY = ['platform', 'fair-use'] as const;

type Share = CapacityLiveDto['share'];

/** "son gönderim dün 14:20": a time in the middle of a Turkish sentence (English keeps "Oct 30" as it is) */
const midSentence = (text: string, locale: string) => (locale.startsWith('tr') ? text.charAt(0).toLocaleLowerCase(locale) + text.slice(1) : text);

export function BireyselCapacity() {
  const live = useQuery({ queryKey: LIVE_KEY, queryFn: () => api<CapacityLiveDto>('GET', '/api/platform/capacity/live'), refetchInterval: 5_000 });
  const data = live.data;
  return (
    <div className="capacity b2c-capacity" data-testid="b2c-capacity">
      <ErrorAlert error={live.error} />
      {!data && live.isPending ? <Skeleton block /> : null}
      {data ? (
        <>
          <LiveTiles data={data} />
          <ShareCard share={data.share} />
          <MailCard mail={data.mail} />
        </>
      ) : null}
      <FairUse />
    </div>
  );
}

function Tile({ title, testId, children }: { title: string; testId: string; children: ReactNode }) {
  return (
    <section className="stat-card" aria-label={title} data-testid={testId}>
      <h2 className="stat-card__label">{title}</h2>
      {children}
    </section>
  );
}

function LiveTiles({ data }: { data: CapacityLiveDto }) {
  const { t, i18n } = useTranslation();
  const locale = i18n.language;
  const { server, requests, queue, tokensToday } = data;
  const tone = server.status === 'up' ? 'success' : server.status === 'down' ? 'danger' : 'quiet';
  const mix = queueMix(queue);
  const seconds = waitSeconds(queue.longestWaitMs);
  const who =
    mix === 'free'
      ? t('b2cPlatform.capacity.queue.allFree')
      : mix === 'pro'
        ? t('b2cPlatform.capacity.queue.allPro')
        : mix === 'mixed'
          ? t('b2cPlatform.capacity.queue.mixed', { pro: queue.pro.toLocaleString(locale), free: queue.free.toLocaleString(locale) })
          : null;
  const waiting = mix === 'none' ? t('b2cPlatform.capacity.queue.none') : [who, seconds > 0 ? t('b2cPlatform.capacity.queue.wait', { seconds }) : null].filter(Boolean).join(' · ');
  const shares = tokenShares(tokensToday);
  return (
    <div className="stat-cards">
      <Tile title={t('b2cPlatform.capacity.server.title')} testId="capacity-tile-server">
        <p className={`stat-card__status stat-card__status--${tone}`} data-status={server.status}>
          <span className="stat-card__dot" aria-hidden="true" />
          {t(`b2cPlatform.capacity.server.status.${server.status}`)}
        </p>
        <p className="stat-card__sub">{server.name ? t('b2cPlatform.capacity.server.named', { name: server.name }) : t('b2cPlatform.capacity.server.shared')}</p>
      </Tile>
      <Tile title={t('b2cPlatform.capacity.requests.title')} testId="capacity-tile-requests">
        <p className="stat-card__value">
          {requests.inFlight.toLocaleString(locale)} / {requests.max.toLocaleString(locale)}
        </p>
        <Progress value={requests.inFlight} max={Math.max(1, requests.max)} label={t('b2cPlatform.capacity.requests.title')} />
        <p className="stat-card__sub">{t('b2cPlatform.capacity.requests.max', { max: requests.max.toLocaleString(locale) })}</p>
        {requests.limit < requests.max ? (
          <p className="stat-card__sub b2c-capacity__busy" data-testid="capacity-busy">
            {t('b2cPlatform.capacity.requests.busy', { limit: requests.limit.toLocaleString(locale) })}
          </p>
        ) : null}
      </Tile>
      <Tile title={t('b2cPlatform.capacity.queue.title')} testId="capacity-tile-queue">
        <p className="stat-card__value">{queue.total.toLocaleString(locale)}</p>
        <p className="stat-card__sub">{waiting}</p>
      </Tile>
      <Tile title={t('b2cPlatform.capacity.tokens.title')} testId="capacity-tile-tokens">
        <p className="stat-card__value">{formatPlanTokens(tokensToday.total, locale)}</p>
        <p className="stat-card__sub">
          {shares
            ? t('b2cPlatform.capacity.tokens.shares', { pro: formatPercent(shares.pro, 100, locale), free: formatPercent(shares.free, 100, locale) })
            : t('b2cPlatform.capacity.tokens.unit')}
        </p>
      </Tile>
    </div>
  );
}

/**
 * "Paylaşım ve sıra": N (1–500), the queue's order and (Phase 16c) the installation's code boxes at once (1–200);
 * what people see is fixed (nothing but the wait). A change not saved says so, so a typed number is never taken for
 * the saved one; every saved change is a row of Denetim kaydı (who, old → new).
 */
function ShareCard({ share }: { share: Share }) {
  const { t } = useTranslation();
  const queryClient = useQueryClient();
  const maxId = useId();
  const maxHintId = useId();
  const orderId = useId();
  const orderHintId = useId();
  const boxesId = useId();
  const boxesHintId = useId();
  const [edits, setEdits] = useState<{ max?: string; order?: Share['queueOrder']; boxes?: string }>({});
  const [done, setDone] = useState(false);
  const maxText = edits.max ?? String(share.maxConcurrent);
  const order = edits.order ?? share.queueOrder;
  const boxesText = edits.boxes ?? String(share.maxSandboxes);
  const max = Number(maxText);
  const boxes = Number(boxesText);
  const maxValid = /^\d+$/.test(maxText) && max >= 1 && max <= 500;
  const boxesValid = /^\d+$/.test(boxesText) && boxes >= 1 && boxes <= 200;
  const dirty = maxText !== String(share.maxConcurrent) || order !== share.queueOrder || boxesText !== String(share.maxSandboxes);
  const save = useMutation({
    mutationFn: () => api<Share>('PUT', '/api/platform/capacity/share', { maxConcurrent: max, queueOrder: order, maxSandboxes: boxes }),
    onSuccess: (saved) => {
      queryClient.setQueryData<CapacityLiveDto>(LIVE_KEY, (old) => (old ? { ...old, share: saved, requests: { ...old.requests, max: saved.maxConcurrent } } : old));
      setEdits({});
      setDone(true);
      void queryClient.invalidateQueries({ queryKey: LIVE_KEY });
    },
  });
  const change = (values: typeof edits) => {
    setEdits((current) => ({ ...current, ...values }));
    setDone(false);
  };
  return (
    <Card title={t('b2cPlatform.capacity.share.title')} className="share-card" data-testid="share-card">
      <div className="share-row">
        <span className="share-row__text">
          <label className="share-row__title" htmlFor={maxId}>
            {t('b2cPlatform.capacity.share.max.title')}
          </label>
          <span id={maxHintId} className="share-row__hint">
            {t('b2cPlatform.capacity.share.max.hint')}
          </span>
        </span>
        <span className="share-row__control">
          <span className="share-number">
            <TextInput
              id={maxId}
              type="number"
              min={1}
              max={500}
              step={1}
              inputMode="numeric"
              value={maxText}
              aria-describedby={maxHintId}
              aria-invalid={!maxValid || undefined}
              onChange={(e) => change({ max: e.target.value })}
              data-testid="share-max"
            />
            <span className="share-number__unit">{t('b2cPlatform.capacity.share.max.unit')}</span>
          </span>
          {!maxValid ? (
            <span className="share-row__error" role="alert">
              {t('b2cPlatform.capacity.share.max.invalid')}
            </span>
          ) : null}
        </span>
      </div>
      <div className="share-row">
        <span className="share-row__text">
          <label className="share-row__title" htmlFor={orderId}>
            {t('b2cPlatform.capacity.share.order.title')}
          </label>
          <span id={orderHintId} className="share-row__hint">
            {t(`b2cPlatform.capacity.share.order.hints.${order}`)}
          </span>
        </span>
        <span className="share-row__control">
          <Select id={orderId} value={order} aria-describedby={orderHintId} onChange={(e) => change({ order: e.target.value === 'arrival' ? 'arrival' : 'pro_first' })} data-testid="share-order">
            <option value="pro_first">{t('b2cPlatform.capacity.share.order.options.pro_first')}</option>
            <option value="arrival">{t('b2cPlatform.capacity.share.order.options.arrival')}</option>
          </Select>
        </span>
      </div>
      <div className="share-row">
        <span className="share-row__text">
          <label className="share-row__title" htmlFor={boxesId}>
            {t('b2cBilling.capacity.boxes.title')}
          </label>
          <span id={boxesHintId} className="share-row__hint">
            {t('b2cBilling.capacity.boxes.hint')}
          </span>
        </span>
        <span className="share-row__control">
          <span className="share-number">
            <TextInput
              id={boxesId}
              type="number"
              min={1}
              max={200}
              step={1}
              inputMode="numeric"
              value={boxesText}
              aria-describedby={boxesHintId}
              aria-invalid={!boxesValid || undefined}
              onChange={(e) => change({ boxes: e.target.value })}
              data-testid="share-boxes"
            />
            <span className="share-number__unit">{t('b2cBilling.capacity.boxes.unit')}</span>
          </span>
          {!boxesValid ? (
            <span className="share-row__error" role="alert">
              {t('b2cBilling.capacity.boxes.invalid')}
            </span>
          ) : null}
        </span>
      </div>
      <div className="share-row">
        <span className="share-row__text">
          <span className="share-row__title">{t('b2cPlatform.capacity.share.shown.title')}</span>
          <span className="share-row__hint">{t('b2cPlatform.capacity.share.shown.hint')}</span>
        </span>
        <span className="share-row__control share-row__value">{t('b2cPlatform.capacity.share.shown.value')}</span>
      </div>
      <ErrorAlert error={save.error} />
      <div className="share-card__foot">
        {done ? (
          <span className="share-card__done" role="status" data-testid="share-saved">
            <Icons.check size={16} />
            {t('common.saved')}
          </span>
        ) : dirty ? (
          <span className="share-card__unsaved" role="status" data-testid="share-unsaved">
            {t('b2cBilling.capacity.unsaved')}
          </span>
        ) : null}
        <Button onClick={() => save.mutate()} disabled={!dirty || !maxValid || !boxesValid} busy={save.isPending} data-testid="share-save">
          {t('common.save')}
        </Button>
      </div>
    </Card>
  );
}

/** D-272: whether the platform's e-mail goes out, and the last error in words (its text has no secrets) */
function MailCard({ mail }: { mail: CapacityLiveDto['mail'] }) {
  const { t, i18n } = useTranslation();
  const times = useTimes();
  const locale = i18n.language;
  const status = mailState(mail);
  const when = (iso: string) => midSentence(times.since(iso), locale);
  return (
    <Card title={t('b2cPlatform.capacity.mail.title')} className="mail-card" data-testid="mail-status">
      {status.state === 'none' ? (
        <p className="mail-status mail-status--none" data-state="none">
          <span className="stat-card__dot" aria-hidden="true" />
          {t('b2cPlatform.capacity.mail.none')}
        </p>
      ) : null}
      {status.state === 'working' ? (
        <p className="mail-status mail-status--working" data-state="working">
          <span className="stat-card__dot" aria-hidden="true" />
          {t('b2cPlatform.capacity.mail.working', { when: when(status.at) })}
        </p>
      ) : null}
      {status.state === 'failing' && mail ? (
        <>
          <p className="mail-status mail-status--failing" data-state="failing">
            <span className="stat-card__dot" aria-hidden="true" />
            {t('b2cPlatform.capacity.mail.failing', { kind: t(`b2cPlatform.capacity.mail.kinds.${status.kind}`), when: when(status.at) })}
          </p>
          <p className="mail-status__more">
            {t('b2cPlatform.capacity.mail.failures', { count: Math.max(1, mail.failuresInRow) })}
            {mail.lastSuccessAt ? ` ${t('b2cPlatform.capacity.mail.lastSuccess', { when: when(mail.lastSuccessAt) })}` : ''}
          </p>
          {mail.lastErrorText ? (
            <p className="mail-status__error" aria-label={t('b2cPlatform.capacity.mail.errorLabel')} data-testid="mail-error">
              {mail.lastErrorText}
            </p>
          ) : null}
        </>
      ) : null}
    </Card>
  );
}

/** D-273: today's heaviest users against their daily limit; the flagged ones get a mail button, nothing more */
function FairUse() {
  const { t, i18n } = useTranslation();
  const locale = i18n.language;
  const zone = useCompanyZone();
  const queryClient = useQueryClient();
  const titleId = useId();
  const rows = useQuery({ queryKey: FAIR_USE_KEY, queryFn: () => api<FairUseRowDto[]>('GET', '/api/platform/fair-use'), refetchInterval: 60_000 });
  const [mailing, setMailing] = useState<FairUseRowDto | null>(null);
  const [done, setDone] = useState<string | null>(null);
  const today = zonedDateKey(new Date(), zone);
  const dayOf = (iso: string) => zonedDateKey(new Date(iso), zone);
  const title = t('b2cPlatform.capacity.fairUse.title');
  const status = (row: FairUseRowDto) =>
    row.flagged ? (
      <Badge tone="warning">{t('b2cPlatform.capacity.fairUse.near', { count: row.streak })}</Badge>
    ) : row.usedUp ? (
      <Badge>{t('b2cPlatform.capacity.fairUse.usedUp')}</Badge>
    ) : (
      <span className="muted">—</span>
    );
  return (
    <section className="admin-card fair-use" aria-labelledby={titleId}>
      <div className="admin-card__head">
        <h2 id={titleId}>{title}</h2>
        <p className="muted">{t('b2cPlatform.capacity.fairUse.note')}</p>
      </div>
      {done ? <Alert tone="success" title={done} data-testid="fair-use-done" /> : null}
      <ErrorAlert error={rows.error} />
      {!rows.data && rows.isPending ? <Skeleton lines={4} /> : null}
      {rows.data ? (
        <div className="dt-frame">
          <ScrollArea label={title} className="dt-scroll">
            <table className="dt fair-use-table" data-testid="fair-use-table">
              <thead>
                <tr>
                  <th scope="col">{t('b2cPlatform.capacity.fairUse.columns.person')}</th>
                  <th scope="col">{t('b2cPlatform.capacity.fairUse.columns.plan')}</th>
                  <th scope="col" aria-sort="descending">
                    <span className="fair-use__sorted">
                      {t('b2cPlatform.capacity.fairUse.columns.today')}
                      <Icons.chevronDown size={14} />
                    </span>
                  </th>
                  <th scope="col" className="dt__num">
                    {t('b2cPlatform.capacity.fairUse.columns.avg7')}
                  </th>
                  <th scope="col">{t('b2cPlatform.capacity.fairUse.columns.status')}</th>
                  <th scope="col" className="dt__actions">
                    <span className="ui-visually-hidden">{t('b2cPlatform.capacity.fairUse.columns.action')}</span>
                  </th>
                </tr>
              </thead>
              <tbody>
                {rows.data.map((row) => {
                  const share = dailyShare(row);
                  return (
                    <tr key={row.userId} data-testid={`fair-use-row-${row.userId}`} data-flagged={row.flagged || undefined}>
                      <td>
                        <span className="person-cell">
                          <span className="person-cell__name">{row.name}</span>
                          <span className="person-cell__email">{row.email}</span>
                        </span>
                      </td>
                      <td>{t(`b2cPlatform.planNames.${row.plan}`)}</td>
                      <td>
                        <span className="fair-use__meter">
                          <Progress
                            value={share}
                            max={100}
                            label={t('b2cPlatform.capacity.fairUse.share', { percent: formatPercent(share, 100, locale) })}
                            tone={share >= 80 ? 'warning' : 'success'}
                          />
                          <span className="fair-use__number">{formatPlanTokens(row.today, locale)}</span>
                        </span>
                      </td>
                      <td className="dt__num">{formatPlanTokens(row.avg7, locale)}</td>
                      <td>
                        <span className="fair-use__status">
                          {status(row)}
                          {row.lastMailAt ? (
                            <span className="fair-use__sent" data-testid={`fair-use-sent-${row.userId}`}>
                              {t('b2cPlatform.capacity.fairUse.sent', { when: humanTime(row.lastMailAt, locale, t, locale.startsWith('tr'), zone) })}
                            </span>
                          ) : null}
                        </span>
                      </td>
                      <td className="dt__actions">
                        {canMailFairUse(row, today, dayOf) ? (
                          <Button size="sm" variant="secondary" onClick={() => setMailing(row)} data-testid={`fair-use-mail-${row.userId}`}>
                            {t('b2cPlatform.capacity.fairUse.mail')}
                          </Button>
                        ) : null}
                      </td>
                    </tr>
                  );
                })}
              </tbody>
            </table>
            {rows.data.length === 0 ? <div className="dt__empty">{t('b2cPlatform.capacity.fairUse.empty')}</div> : null}
          </ScrollArea>
        </div>
      ) : null}
      <p className="fair-use__foot">{t('b2cPlatform.capacity.fairUse.footnote')}</p>
      {mailing ? (
        <FairUseMailDialog
          row={mailing}
          onClose={() => setMailing(null)}
          onSent={(sentAt) => {
            queryClient.setQueryData<FairUseRowDto[]>(FAIR_USE_KEY, (old) => old?.map((r) => (r.userId === mailing.userId ? { ...r, lastMailAt: sentAt } : r)));
            void queryClient.invalidateQueries({ queryKey: FAIR_USE_KEY });
            setDone(t('b2cPlatform.capacity.fairUse.done', { name: mailing.name }));
            setMailing(null);
          }}
        />
      ) : null}
    </section>
  );
}

/** The fair use mail as it will go out (GET), then "Gönder" (POST); both are the API's template, never typed here */
function FairUseMailDialog({ row, onClose, onSent }: { row: FairUseRowDto; onClose: () => void; onSent: (sentAt: string) => void }) {
  const { t } = useTranslation();
  const preview = useQuery({
    queryKey: ['platform', 'fair-use-mail', row.userId],
    queryFn: () => api<{ to: string; subject: string; text: string }>('GET', `/api/platform/users/${row.userId}/fair-use-mail`),
    gcTime: 0,
  });
  const send = useMutation({
    mutationFn: () => api<{ sentAt: string }>('POST', `/api/platform/users/${row.userId}/fair-use-mail`),
    onSuccess: (result) => onSent(result.sentAt),
  });
  const mail = preview.data;
  return (
    <Dialog
      title={t('b2cPlatform.capacity.fairUse.dialog.title')}
      onClose={onClose}
      closeLabel={t('common.close')}
      data-testid="fair-use-dialog"
      footer={
        <>
          <Button variant="secondary" onClick={onClose}>
            {t('common.cancel')}
          </Button>
          <Button icon={<Icons.send size={16} />} disabled={!mail} busy={send.isPending} onClick={() => send.mutate()} data-testid="fair-use-send">
            {t('b2cPlatform.capacity.fairUse.dialog.send')}
          </Button>
        </>
      }
    >
      <div className="dialog-form">
        <p className="muted">{t('b2cPlatform.capacity.fairUse.dialog.intro')}</p>
        <ErrorAlert error={preview.error ?? send.error} />
        {mail ? (
          <dl className="mail-preview" data-testid="fair-use-preview">
            <div className="mail-preview__row">
              <dt>{t('b2cPlatform.capacity.fairUse.dialog.to')}</dt>
              <dd>{mail.to}</dd>
            </div>
            <div className="mail-preview__row">
              <dt>{t('b2cPlatform.capacity.fairUse.dialog.subject')}</dt>
              <dd>{mail.subject}</dd>
            </div>
            <div className="mail-preview__row mail-preview__row--body">
              <dt>{t('b2cPlatform.capacity.fairUse.dialog.text')}</dt>
              <dd>
                <ScrollArea label={t('b2cPlatform.capacity.fairUse.dialog.text')} className="mail-preview__text">
                  {mail.text}
                </ScrollArea>
              </dd>
            </div>
          </dl>
        ) : preview.isPending ? (
          <div role="status" aria-label={t('b2cPlatform.capacity.fairUse.dialog.loading')}>
            <Skeleton lines={5} />
          </div>
        ) : null}
      </div>
    </Dialog>
  );
}
